import { Storage } from "@/services/Storage";
import axios from "axios";
import connectivityMonitor from '@/services/connectivityMonitor';
// Ticket #26135 / AB#26135 — paginated /stock/{DB}/page helpers (mirror Retail POS).
// docs/design-docs/2026-08-04-ticket-26135-restaurant-stock-pagination-fix.md
import {
    STOCK_PAGE_SIZE,
    STOCK_FETCH_PAGE_TIMEOUT_MS,
    parseStockPagePayload,
    accumulateStockPages,
} from './stockPagination';

const STOCK_SYNC_CHUNK_SIZE = 500;
const FULL_SYNC_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const LARGE_SORT_THRESHOLD = 5000;
const HIGH_VOLUME_STOCK_THRESHOLD = 20000;
const MOBILE_FASTLINE_CAP = 2000;
const MOBILE_STOCK_CACHE_CAP = 5000;
const normalizeString = (value) => String(value || '').toLowerCase();
const sortByDescription = (a, b) => String(a?.Description || '').localeCompare(String(b?.Description || ''));

const pauseBriefly = () => new Promise((resolve) => setTimeout(resolve, 0));
const isNativeMobileRuntime = () => {
    try {
        if (window?.androidMode) return true;
        return !!(window?.Capacitor &&
            typeof window.Capacitor.isNativePlatform === 'function' &&
            window.Capacitor.isNativePlatform());
    } catch (_) {
        return !!window?.androidMode;
    }
};

const dedupeByPlu = (items = []) => {
    const source = Array.isArray(items) ? items : [];
    const unique = new Map();
    for (let i = 0; i < source.length; i++) {
        const item = source[i];
        const key = normalizeString(item?.PLU);
        if (!key || unique.has(key)) continue;
        unique.set(key, item);
    }
    return Array.from(unique.values());
};

const compactStockCacheItem = (item = {}) => ({
    PLU: item.PLU,
    Description: item.Description,
    SellingIncl: item.SellingIncl,
    TaxLabels: item.TaxLabels,
    TaxLabel: item.TaxLabel,
    taxLabel: item.taxLabel,
    Tax: item.Tax,
    RecommendedRetail: item.RecommendedRetail,
    recommendedRetail: item.recommendedRetail,
    StockSetting: item?.StockSetting ? { mtv: item.StockSetting.mtv } : undefined
});

const buildStockSettingMap = (settings = []) => {
    const source = Array.isArray(settings) ? settings : [];
    return source.reduce((acc, setting) => {
        const key = normalizeString(setting?.plu || setting?.PLU);
        if (!key) return acc;
        acc[key] = setting;
        return acc;
    }, {});
};

const sanitizeStockItem = (item) => {
    const clean = { ...(item || {}) };
    delete clean.CreatedDate;
    delete clean.Image;
    delete clean.Price1;
    delete clean.Price2;
    delete clean.Price3;
    delete clean.Price4;
    delete clean.Price5;
    delete clean.args;
    delete clean.where_between;
    return clean;
};

const mapStockSetting = (stockItem) => {
    const set = stockItem?.StockSetting;
    if (set) {
        return {
            plu: stockItem.PLU,
            cookingLink: set.CookingLink,
            mtv: set.mtv,
            askForPrice: set.askForPrice,
            askForDescription: set.askForDescription,
            askForBoth: set.askForBoth,
            NonStock: set.NonStock,
            tourism: set.tourism,
            getQtyFromPrice: set.getQtyFromPrice,
            scale: set.Scale || set.scale,
            lpoVatPreprocessed: set.lpoVatPreprocessed,
            password_protected: set.password_protected,
            printer: set.printer,
            StockCode: set.StockCode,
            Made: set.Made,
            minLevel: set.minLevel,
            maxLevel: set.maxLevel,
            isACase: set.isACase,
            fromACase: set.fromACase
        };
    }

    return {
        plu: stockItem.PLU,
        mtv: 0.00,
        tourism: false,
        NonStock: false,
        cookingLink: false,
        askForPrice: false,
        askForDescription: false,
        askForBoth: false,
        printer: null,
        StockCode: stockItem.PLU,
        Made: false,
        minLevel: 0,
        maxLevel: 0,
        isACase: false,
        fromACase: false,
        lpoVatPreprocessed: false
    };
};

const parseSyncTime = (value) => {
    if (!value) return 0;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getStockItemSyncTime = (item = {}) => {
    return parseSyncTime(
        item?.updated_at ||
        item?.updatedAt ||
        item?.UpdatedAt ||
        item?.UpdatedDate ||
        item?.updatedDate ||
        item?.ModifiedDate
    );
};

const shouldKeepDirtyLocalVersion = (localItem = {}, incomingItem = {}, dirtyState = null) => {
    if (!dirtyState) return false;
    const localSyncTime = getStockItemSyncTime(localItem);
    const incomingSyncTime = getStockItemSyncTime(incomingItem);
    if (localSyncTime && incomingSyncTime) {
        return localSyncTime > incomingSyncTime;
    }
    return localSyncTime > 0 && incomingSyncTime === 0;
};

const isFullSyncDue = (syncState = {}) => {
    const lastFullSyncAt = parseSyncTime(syncState?.lastFullSyncAt);
    if (!lastFullSyncAt) return true;
    return (Date.now() - lastFullSyncAt) >= FULL_SYNC_MAX_AGE_MS;
};

const normalizeStockResponse = (data) => {
    if (Array.isArray(data)) {
        return {
            ok: true,
            items: data,
            isDelta: false,
            modeExplicit: false
        };
    }

    if (!data || typeof data !== 'object') {
        return { ok: false, items: [], isDelta: false, modeExplicit: false };
    }

    const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.stock)
            ? data.stock
            : Array.isArray(data.rows)
                ? data.rows
                : Array.isArray(data.data)
                    ? data.data
                    : null;

    if (!Array.isArray(items)) {
        return { ok: false, items: [], isDelta: false, modeExplicit: false };
    }

    const hasModeHint = (
        data.mode !== undefined ||
        data.syncType !== undefined ||
        data.delta !== undefined ||
        data.isDelta !== undefined
    );
    const modeText = String(data.mode || data.syncType || '').toLowerCase();
    const isSnapshotHint = modeText.includes('snapshot') || modeText.includes('full');
    const isDeltaHint = (
        data.delta === true ||
        data.isDelta === true ||
        modeText.includes('delta') ||
        modeText.includes('incremental') ||
        modeText.includes('change')
    );

    return {
        ok: true,
        items,
        isDelta: isSnapshotHint ? false : isDeltaHint,
        modeExplicit: hasModeHint
    };
};

const mergeStockChanges = (existing = [], changes = [], dirtyPluState = {}) => {
    const map = new Map();

    for (let i = 0; i < existing.length; i++) {
        const item = existing[i];
        const pluKey = String(item?.PLU || '').toLowerCase();
        if (!pluKey) continue;
        map.set(pluKey, item);
    }

    for (let i = 0; i < changes.length; i++) {
        const change = changes[i];
        const pluKey = String(change?.PLU || '').toLowerCase();
        if (!pluKey) continue;

        const previous = map.get(pluKey) || {};
        const dirtyState = dirtyPluState?.[pluKey] || null;
        if (shouldKeepDirtyLocalVersion(previous, change, dirtyState)) {
            continue;
        }

        map.set(pluKey, {
            ...previous,
            ...change,
            StockSetting: change?.StockSetting !== undefined ? change.StockSetting : previous?.StockSetting
        });
    }

    return [...map.values()];
};

const reapplyDirtyStockDeltas = (snapshot = [], dirtyPluState = {}) => {
    const dirtyKeys = Object.keys(dirtyPluState || {});
    if (!Array.isArray(snapshot) || snapshot.length === 0 || dirtyKeys.length === 0) {
        return snapshot;
    }

    return snapshot.map((item) => {
        const pluKey = String(item?.PLU || '').toLowerCase();
        if (!pluKey) return item;

        const dirtyState = dirtyPluState[pluKey];
        if (!dirtyState) return item;

        const deltaQty = Number(dirtyState?.qtyDelta ?? dirtyState?.delta ?? 0);
        if (!Number.isFinite(deltaQty) || deltaQty <= 0) return item;

        const currentQty = Number(item?.QTY ?? item?.Qty ?? item?.qty ?? 0);
        if (!Number.isFinite(currentQty)) return item;

        const nextQty = Number((currentQty - deltaQty).toFixed(3));
        const updatedAt = dirtyState?.updatedAt || item?.UpdatedAt || item?.updatedAt;
        const patched = {
            ...item,
            QTY: nextQty,
            ...(updatedAt ? { UpdatedAt: updatedAt, updatedAt } : {})
        };
        if (item?.Qty !== undefined) patched.Qty = nextQty;
        if (item?.qty !== undefined) patched.qty = nextQty;
        return patched;
    });
};

const prepareStockData = async (items = [], options = {}) => {
    const buildSettings = options?.buildSettings !== false;
    const stock = Array.isArray(items) ? items : [];
    const stockSettings = buildSettings ? [] : null;

    for (let i = 0; i < stock.length; i += STOCK_SYNC_CHUNK_SIZE) {
        const end = Math.min(i + STOCK_SYNC_CHUNK_SIZE, stock.length);

        for (let j = i; j < end; j++) {
            const row = stock[j] || {};
            stock[j] = sanitizeStockItem(row);
            if (buildSettings) {
                stockSettings.push(mapStockSetting(row));
            }
        }

        if (end < stock.length) {
            await pauseBriefly();
        }
    }

    return { stock, stockSettings: stockSettings || [] };
};

export default {
    name: 'stock',
    state: {
        stock: [],
        stockProducts: [],
        activeStockProducts: [],
        stockSetting: [],
        stockSettingMap: {},
        stockItem: {},
        stockCache: new Map(),
        stockLength: 1,
        limit: 30
    },
    getters: {
        getStockProducts: (state) => state.stockProducts,
        getActiveStockProducts: (state) => state.activeStockProducts,
        getAllStockProducts: ({ stockCache }) => [...stockCache.values()],
        getStockItem: (state) => state.stockItem,
        getStockSetting: (state) => state.stockSetting,
        getStockSettingByPlu: (state) => (plu) => {
            const key = normalizeString(plu);
            if (!key) return null;
            return state.stockSettingMap[key] || null;
        },
        getStockLength: (state) => state.stockLength,
        getLimit: (state) => state.limit,
        getByPlu: (state) => (plu) => {
            if (plu === undefined || plu === null) return null;
            const key = normalizeString(plu);
            const item = state.stockCache.get(key);
            if (!item) {
                return state.stock.find(stockItem => normalizeString(stockItem?.PLU) === key);
            }
            return item;
        },
    },

    mutations: {
        setLimit: (state, payload) => {
            state.limit = payload;
        },
        /**
         * In-memory only empty after Storage.clearLocalStock already ran.
         * Avoids async stockByPLU([]) race on logout / store switch.
         */
        resetLocalStockState: (state) => {
            state.stock = [];
            state.stockProducts = [];
            state.activeStockProducts = [];
            state.stockCache = new Map();
            state.stockSetting = [];
            state.stockSettingMap = {};
            state.stockItem = {};
        },
        stockByPLU: async (state, payload) => {
            if (payload) {
                const source = Array.isArray(payload) ? payload : [];
                await Storage.stockByPLU(source);

                // Large stock payloads can be 100k+ rows on mobile; avoid full-array sort/copy here.
                const highVolumeMobile = isNativeMobileRuntime() && source.length > HIGH_VOLUME_STOCK_THRESHOLD;
                const fastLineMap = new Map();
                const stockCache = new Map();
                for (let i = 0; i < source.length; i++) {
                    const item = source[i];
                    const key = normalizeString(item?.PLU);
                    if (!key) continue;

                    if (highVolumeMobile) {
                        // On high-volume mobile, keep only fast-line SKUs in cache to avoid OOM.
                        if (item?.FastLine === 1) {
                            stockCache.set(key, compactStockCacheItem(item));
                        }
                    } else {
                        stockCache.set(key, item);
                    }
                    if (item?.FastLine === 1 && !fastLineMap.has(key)) {
                        fastLineMap.set(key, item);
                    }
                }

                let dedupedFastLine = [...fastLineMap.values()];
                if (highVolumeMobile && dedupedFastLine.length > MOBILE_FASTLINE_CAP) {
                    dedupedFastLine = dedupedFastLine.slice(0, MOBILE_FASTLINE_CAP);
                }
                if (dedupedFastLine.length <= LARGE_SORT_THRESHOLD) {
                    dedupedFastLine.sort(sortByDescription);
                }

                state.stock = dedupedFastLine;
                state.stockProducts = dedupedFastLine;
                state.activeStockProducts = dedupedFastLine.filter(item => item?.archived === false);
                state.stockCache = stockCache;

            } else {
                await Storage.getFastLine().then(res => {
                    const source = Array.isArray(res) ? res : [];
                    const deduped = dedupeByPlu(source);
                    const list = deduped.length > LARGE_SORT_THRESHOLD
                        ? deduped
                        : deduped.slice().sort(sortByDescription);

                    state.stock = list;
                    state.stockProducts = list;
                    state.activeStockProducts = list.filter(item => item?.archived === false);
                })
            }
        },
        setStockSetting: async (state, payload) => {
            if (payload) {
                const safePayload = Array.isArray(payload) ? payload : [];
                state.stockSetting = safePayload;
                state.stockSettingMap = buildStockSettingMap(safePayload);
            }
        },
        mergeStockSetting: (state, payload) => {
            if (!payload) return;
            const key = normalizeString(payload?.plu || payload?.PLU);
            if (!key) return;

            const existing = state.stockSettingMap[key] || null;
            const merged = {
                ...(existing || {}),
                ...payload,
                plu: payload?.plu || payload?.PLU || existing?.plu || existing?.PLU
            };

            state.stockSettingMap = {
                ...state.stockSettingMap,
                [key]: merged
            };

            const nextSettings = Array.isArray(state.stockSetting) ? state.stockSetting.slice() : [];
            const idx = nextSettings.findIndex(setting => normalizeString(setting?.plu || setting?.PLU) === key);
            if (idx >= 0) {
                nextSettings.splice(idx, 1, merged);
            } else {
                nextSettings.push(merged);
            }
            state.stockSetting = nextSettings;
        },
        setSearch: (state, payload) => {
            const source = dedupeByPlu(Array.isArray(payload) ? payload : []);
            const list = source.length > LARGE_SORT_THRESHOLD
                ? source
                : source.slice().sort(sortByDescription);
            state.stock = list;
            state.stockProducts = list;
            state.activeStockProducts = list.filter(item => item?.archived === false);

            // Keep recently searched items in cache for getByPlu lookups (used by VAT/cooking flows).
            const cache = state.stockCache instanceof Map ? state.stockCache : new Map();
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                const key = normalizeString(item?.PLU);
                if (!key) continue;
                cache.set(key, item);
            }
            if (isNativeMobileRuntime()) {
                while (cache.size > MOBILE_STOCK_CACHE_CAP) {
                    const firstKey = cache.keys().next().value;
                    if (firstKey === undefined) break;
                    cache.delete(firstKey);
                }
            }
            state.stockCache = cache;
        },
        setStockItem: (state, payload) => {
            state.stockItem = payload
        },
        setStockLength: (state, payload) => {
            state.stockLength = payload
        },
        addStockItem: (state, payload) => {
            Storage.getAllStock().then(stock => {
                state.stock = state.stock.map(s => {
                    if (s.PLU === payload.PLU) {
                        return payload;
                    }
                    return s;
                })
                return Storage.stockByPLU(state.stock);
            });
        }
    },
    actions: {
        addStockItem: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            try {
                delete payload.CreatedUser
            } catch (e) {
                console.error(e)
            }
            return axios.post(`${serverApi}stock/${store.StoreDB}`, payload)
                .then(({ data }) => {
                    context.commit('addStockItem', data);
                    context.commit('setStockItem', data);
                    return data;
                })
                .catch((err) => errorHandler.tomcatError(err)).finally(() => context.dispatch('getAllStock'))
        },
        getStockSetting: async (context, plu) => {
            const getByPlu = context.getters.getStockSettingByPlu;
            if (typeof getByPlu === 'function') {
                const cached = getByPlu(plu);
                if (cached) return cached;
            }

            const itemLookup = context.getters.getByPlu;
            if (typeof itemLookup === 'function') {
                const stockItem = itemLookup(plu);
                if (stockItem?.StockSetting) {
                    const mapped = mapStockSetting(stockItem);
                    context.commit('mergeStockSetting', mapped);
                    return mapped;
                }
            }

            const setting = await Storage.getStockSetting(plu);
            if (setting && (setting.plu || setting.PLU)) {
                context.commit('mergeStockSetting', setting);
            }
            return setting;
        },
        /**
         * Fetch full/delta stock catalogue for the default store and hydrate Vuex + Storage.
         *
         * Ticket #26135 / AB#26135 — full snapshot no longer hits legacy bulk
         * `GET /stock/{DB}` (500/OOM on large wholesale catalogues via
         * StockMinifier.getAllStockV2). Full sync mirrors Retail POS:
         * `GET /stock/{DB}/page?page=&size=1000` looped until short/empty page.
         *
         * Delta path unchanged: same legacy URL with `since` / `syncMode=delta`
         * query params; on failure falls back to paginated full sync.
         *
         * Error policy for pagination (match Retail Phase 2):
         * mid-loop page failure keeps items already accumulated (`partial`);
         * page-0 failure throws into the outer catch → cache / tomcatError.
         *
         * Return shape: void (callers only rely on side effects — stockByPLU commit).
         * Reference: Retail `src/store/modules/stock/stockmodule.js` fetchStockPage /
         * runBackgroundStockPages (size=1000, terminate on items.length < size).
         * docs/design-docs/2026-08-04-ticket-26135-restaurant-stock-pagination-fix.md
         *
         * @param {Object} context Vuex action context
         * @returns {Promise<void>}
         */
        getAllStock: async (context) => {
            let store = await Storage.getDefaultStore();
            let device = await Storage.getDeviceSettings();
            let user = await Storage.getCurrentUser();
            if (!store) return;

            // Check if online
            const isOnline = connectivityMonitor.getStatus();

            if (!isOnline) {
                // Offline: Check if stock exists in storage
                console.log('[Stock Module] Offline - checking for cached stock');
                const cachedStock = await Storage.getAllStock();
                if (cachedStock && cachedStock.length > 0) {
                    console.log('[Stock Module] Using cached stock:', cachedStock.length, 'items');
                    context.commit('stockByPLU', cachedStock);
                    return;
                } else {
                    console.warn('[Stock Module] No cached stock available. Stock fetch skipped while offline.');
                    return;
                }
            }

            const storeDb = store.StoreDB;
            // Legacy URL kept for delta-only attempts (since/syncMode params).
            const legacyUrl = serverApi + `stock/${storeDb}`;
            // Paginated full sync — Ticket #26135 (mirror Retail /page pattern).
            const pageUrl = serverApi + `stock/${storeDb}/page`;
            const baseParams = { pc: device?.PC, user: user ? user.Username : undefined };
            const syncState = await Storage.getStockSyncState(storeDb) || {};
            const forceFullSync = isFullSyncDue(syncState);
            const nowIso = new Date().toISOString();

            await Storage.setStockSyncState(storeDb, {
                lastSyncAttemptAt: nowIso
            });

            /**
             * Attempt incremental sync via legacy endpoint + since/syncMode=delta.
             * Unchanged contract for callers; failures fall through to paginated full.
             * @returns {Promise<null|{ok:boolean,items:Array,isDelta:boolean,modeExplicit:boolean}>}
             */
            const attemptDeltaFetch = async () => {
                if (forceFullSync || !syncState?.lastSuccessfulSyncAt) return null;
                try {
                    const deltaParams = {
                        ...baseParams,
                        since: syncState.lastSuccessfulSyncAt,
                        updatedAt: syncState.lastSuccessfulSyncAt,
                        updated_at: syncState.lastSuccessfulSyncAt,
                        lastSyncAt: syncState.lastSuccessfulSyncAt,
                        syncMode: 'delta',
                        delta: true
                    };
                    const { data } = await axios.get(legacyUrl, { params: deltaParams });
                    const normalized = normalizeStockResponse(data);
                    if (!normalized.ok) return null;

                    // If backend doesn't provide explicit mode, prefer delta-merge to avoid accidental data loss.
                    if (!normalized.modeExplicit && normalized.items.length > 0) {
                        normalized.isDelta = true;
                    }

                    return normalized;
                } catch (error) {
                    console.warn('[Stock Module] Delta stock sync failed. Falling back to paginated full sync.');
                    return null;
                }
            };

            /**
             * Full catalogue via GET /stock/{DB}/page (size=1000), looped.
             * @returns {Promise<{ok:boolean,items:Array,isDelta:boolean,modeExplicit:boolean,partial?:boolean}>}
             */
            const fetchPaginatedFullStock = async () => {
                const pageResult = await accumulateStockPages({
                    pageSize: STOCK_PAGE_SIZE,
                    fetchPage: async (page, size) => {
                        const { data } = await axios.get(pageUrl, {
                            params: {
                                ...baseParams,
                                page,
                                size,
                            },
                            timeout: STOCK_FETCH_PAGE_TIMEOUT_MS,
                        });
                        return parseStockPagePayload(data, size);
                    },
                });

                if (pageResult.partial) {
                    console.warn(
                        `[Stock Module] Paginated full sync partial after ${pageResult.pagesFetched} page(s); keeping ${pageResult.items.length} items (Ticket #26135 / Retail mid-loop policy)`
                    );
                } else {
                    console.log(
                        `[Stock Module] Paginated full sync complete: ${pageResult.items.length} items in ${pageResult.pagesFetched} page(s)`
                    );
                }

                return {
                    ok: true,
                    items: Array.isArray(pageResult.items) ? pageResult.items : [],
                    isDelta: false,
                    modeExplicit: true,
                    partial: pageResult.partial === true,
                };
            };

            try {
                const deltaResult = await attemptDeltaFetch();
                let normalizedResponse = deltaResult;

                if (!normalizedResponse) {
                    // Ticket #26135: paginated /page instead of legacy bulk GET /stock/{DB}
                    normalizedResponse = await fetchPaginatedFullStock();
                    if (!normalizedResponse.ok) {
                        throw new Error('Invalid stock response payload');
                    }
                }

                const incomingItems = Array.isArray(normalizedResponse.items) ? normalizedResponse.items : [];
                let stockSnapshot = incomingItems;
                let syncMode = normalizedResponse.isDelta ? 'delta' : 'snapshot';
                const dirtyPluState = await Storage.getStockDirtyPlus(storeDb) || {};

                if (normalizedResponse.isDelta) {
                    const existingStock = await Storage.getRawStock();
                    stockSnapshot = mergeStockChanges(existingStock || [], incomingItems || [], dirtyPluState);
                } else if (Object.keys(dirtyPluState).length > 0) {
                    // Preserve unsynced local sale deductions during full snapshot refresh.
                    stockSnapshot = reapplyDirtyStockDeltas(incomingItems, dirtyPluState);
                }

                const shouldPersistAllSettings = !(isNativeMobileRuntime() && stockSnapshot.length > HIGH_VOLUME_STOCK_THRESHOLD);
                const { stock, stockSettings } = await prepareStockData(stockSnapshot, {
                    buildSettings: shouldPersistAllSettings
                });
                await context.commit('stockByPLU', stock);
                if (!shouldPersistAllSettings) {
                    // Avoid loading 100k settings into Vuex on Android; use on-demand setting lookup.
                    await Storage.setStockSettings([]);
                } else {
                    await Storage.setStockSettings(stockSettings);
                }

                if (isNativeMobileRuntime() && stock.length > HIGH_VOLUME_STOCK_THRESHOLD) {
                    // Building 10k+ image URL index can trigger OOM on low-memory Android devices.
                    await Storage.setStockImages([]);
                } else {
                    await context.dispatch(`setStockImages`, stock);
                }

                const syncCompleteTime = new Date().toISOString();
                await Storage.setStockSyncState(storeDb, {
                    lastSuccessfulSyncAt: syncCompleteTime,
                    lastSyncMode: syncMode,
                    lastSyncCount: stock.length,
                    lastSyncError: null,
                    ...(syncMode === 'snapshot' ? { lastFullSyncAt: syncCompleteTime } : {})
                });
            } catch (err) {
                console.warn('[Stock Module] Failed to fetch stock from server');
                await Storage.setStockSyncState(storeDb, {
                    lastSyncError: String(err?.message || err || 'Unknown stock sync error')
                });

                const cachedStock = await Storage.getAllStock();
                if (cachedStock && cachedStock.length > 0) {
                    console.log('[Stock Module] Using cached stock after fetch failure');
                    context.commit('stockByPLU', cachedStock);
                    return;
                }
                return errorHandler.tomcatError(err);
            }
        },
        searchStock: (context, { query = {}, limit, offset }) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            context.commit('setCurrentCategory', query.category)
            context.commit('setSubCategory', query.subCategory)
            context.commit('setSubCategory2', query.sub2Category)
            return Storage.searchStock(query, limit, offset).then(res => {
                context.commit('setSearch', res)
            }).catch(err => console.error(err))
        },
        searchDescription: (context, query) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            context.commit('setCurrentCategory', query.category)
            context.commit('setSubCategory', query.subCategory)
            context.commit('setSubCategory2', query.sub2Category)
            return Storage.searchStockDesc(query).then(res => {
                context.commit('setSearch', res)
            }).catch(err => console.error(err))
        },
        searchPartialDescription: (context, query) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            return Storage.searchStockDesc(query, 5).then(res => {
                return res;
            }).catch(err => console.error(err))
        },

        searchDepartment: async (context, query) => {
            let search = context.getters.getSearchCategories;
            query.category = search.category;
            query.subCategory = search.subCategory;
            query.sub2Category = search.sub2Category;

            query.description = '';
            return Storage.searchStockDesc(query).then((value => value));
        },

        getFastLine: (context, query) => {
            context.commit(`setSearchCategories`, {
                category: ``,
                subCategory: ``,
                sub2Category: ``
            })
            return Storage.getFastLine().then(res => {
                let list = Array.isArray(res) ? res : [];
                if (isNativeMobileRuntime() && list.length > MOBILE_FASTLINE_CAP) {
                    console.warn(`[Stock Module] FastLine list too large for mobile (${list.length}). Capping to ${MOBILE_FASTLINE_CAP}.`);
                    list = list.slice(0, MOBILE_FASTLINE_CAP);
                }
                context.commit('setSearch', list)
            }).catch(err => console.error(err))
        },

        setStockImages: async (context, stockPayload) => {
            let store = await Storage.getDefaultStore();
            let stockItems = Array.isArray(stockPayload) ? stockPayload : await Storage.getRawStock();

            // Fallback to in-memory cache if storage write has not finished yet.
            if ((!Array.isArray(stockItems) || stockItems.length === 0) && context.state?.stockCache?.size) {
                stockItems = [...context.state.stockCache.values()];
            }

            if (!store || !Array.isArray(stockItems) || stockItems.length === 0) return;
            if (isNativeMobileRuntime() && stockItems.length > HIGH_VOLUME_STOCK_THRESHOLD) {
                await Storage.setStockImages([]);
                return;
            }

            const itemsWithImages = stockItems.filter(stock => stock.hasImage);
            if (itemsWithImages.length === 0) {
                await Storage.setStockImages([]);
                context.commit(`stockByPLU`);
                return;
            }

            const baseUrl = String(franchiseTomcatApi || '').endsWith('/')
                ? String(franchiseTomcatApi)
                : `${franchiseTomcatApi}/`;
            const storeDb = encodeURIComponent(store.StoreDB || '');
            const images = itemsWithImages.map((item) => ({
                PLU: item.PLU,
                Image: `${baseUrl}stock/${storeDb}/image?plu=${encodeURIComponent(item.PLU)}`
            }));

            await Storage.setStockImages(images);
            context.commit(`stockByPLU`);
        },
    }
}
